// Central configuration for URL-driven filters.
//
// The URL is the single source of truth for every filter in the app. This file
// defines the canonical query-param keys, the global-filter options, and the
// helpers used by the `useFilters` hook to (de)serialize filters to/from the
// URL.

// Predefined global-filter options (moved out of the Dropdown component so the
// hook can resolve a `{ value, label }` object from a bare URL value).
export const GLOBAL_FILTER_OPTIONS = [
  { value: "all", label: "All Hospitals" },
  { value: "coc_type", label: "CoC Accrediated" },
  { value: "aamc", label: "AAMC Hospitals" },
  { value: "non_aamc_community_hospitals", label: "Non-AAMC Hospitals" },
];

export const DEFAULT_GLOBAL_FILTER = GLOBAL_FILTER_OPTIONS[0];

// Canonical query-param keys for the non-advanced filters. These names match
// what the backend already expects on its API calls.
export const FILTER_KEYS = {
  GLOBAL_FILTER: "globalFilter",
  QUICK_FILTER: "quickFilter",
  PARTNER_EXCLUSIVE: "partnerExclusiveArea",
  // Single selected state (abbreviation). Only one state is ever selected via
  // the URL now (hex click / state search), so the param is singular `state`.
  // A legend selection is expressed via `legendFilter` and its states are
  // derived, not stored here.
  STATE: "state",
  LEGEND_FILTER: "legendFilter",
  SORT_BY: "sort_by",
  SORT_ORDER: "sort_order",
  // Active homepage screen (map | table | compare). Persisted so a refresh
  // restores the same view. Reserved so it is never sent to the API, counted as
  // an applied filter, or cleared by the advanced/global-filter setters.
  VIEW: "view",
  // The applied search term (what the user typed in the global search box). The
  // URL is the single source of truth for search too, so a refresh/shared link
  // restores the results. It is translated to the backend `search_term` (text
  // search) or resolved to a `state` selection (state search) by the URL
  // builder; the raw `q` value itself is never sent to the API.
  SEARCH: "q",
};

// Backend APIs still expect the plural `states` query param, even though the
// URL stores a single `state`. Kept separate so the two never get confused.
export const API_STATES_PARAM = "states";

// Any URL param that is NOT one of these reserved keys is treated as an
// "advanced filter" field. This keeps the advanced-filter handling robust to
// whatever field keys the advanced filter form produces (one param per field).
export const RESERVED_FILTER_KEYS = new Set([
  FILTER_KEYS.GLOBAL_FILTER,
  FILTER_KEYS.QUICK_FILTER,
  FILTER_KEYS.PARTNER_EXCLUSIVE,
  FILTER_KEYS.STATE,
  FILTER_KEYS.LEGEND_FILTER,
  FILTER_KEYS.SORT_BY,
  FILTER_KEYS.SORT_ORDER,
  FILTER_KEYS.VIEW,
  FILTER_KEYS.SEARCH,
]);

// Advanced numeric range fields. Used to sanitize hand-edited URLs (drop
// non-numeric values) so a malformed URL can't break API calls.
export const ADVANCED_NUMERIC_KEYS = new Set([
  "bed_count_min",
  "bed_count_max",
  "square_footage_min",
  "square_footage_max",
  "patient_discharge_min",
  "patient_discharge_max",
  "net_income_margin_min",
  "net_income_margin_max",
  "net_patient_revenue_min",
  "net_patient_revenue_max",
]);

// Whitelisted legend-filter values (density buckets + partner locations).
export const LEGEND_FILTER_VALUES = new Set(["low", "medium", "high", "partner"]);

// Normalizes an applied advanced-filter object into a flat map of
// string-serializable values, dropping empties and unwrapping the
// `coc_accredited` checkbox object. Numeric range fields with non-numeric
// values are dropped.
export const sanitizeAdvancedFilters = (filtersObj = {}) => {
  const result = {};
  Object.entries(filtersObj || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    // Unwrap the coc_accredited checkbox: { checked, value }
    if (typeof value === "object") {
      if ("checked" in value) {
        if (value.checked && value.value) result[key] = value.value;
        return;
      }
      if ("value" in value && value.value) {
        result[key] = value.value;
        return;
      }
      return;
    }

    if (ADVANCED_NUMERIC_KEYS.has(key)) {
      const num = Number(value);
      if (Number.isNaN(num)) return;
      result[key] = num;
      return;
    }

    result[key] = value;
  });
  return result;
};
